import React from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import CalendarDayPopup, { type PopupRoomType } from './CalendarDayPopup'
import PaceDot from './PaceDot'
import { cn } from '@/lib/utils'
import { currencySymbol, formatRateSym } from '@/lib/calendar-utils'
import { hasTargetAdjustment } from '@/lib/target-utils'
import { overridePct, overridePctLabel } from '@/lib/pipeline-utils'
import { depthBadgeLabel } from '@/lib/clearance-utils'

interface ComputationDetails {
  base_rate_amount?: number
  adjustments?: Array<{
    stage: string
    type: string
    value?: number
    description: string
  }>
  [key: string]: unknown
}

interface RateData {
  id: number
  rateAmount: number
  source: string
  stopSell: boolean
  closedToArrival: boolean
  closedToDeparture: boolean
  minStayArrival: number | null
  maxStay: number | null
  computationDetails: ComputationDetails
  clearanceOffsetPct?: number | null
  pushedRateAmount: number | null
  syncStatus: 'synced' | 'pending' | 'never_pushed'
}

interface OccupancyData {
  booked: number
  available: number
  occupancyPct: number
  pickup7d: number | null
  pickup7dRooms?: number | null
  paceRatio?: number | null
  baselineOccPct?: number | null
}

interface PropertyOccupancyData {
  booked: number
  available: number
  occupancyPct: number
  /** What the pooled figure is called: "Building" at a combined inventory (server); absent means "Hotel". */
  label?: string | null
}

interface RateCellProps {
  /** Today AT THE PROPERTY (YYYY-MM-DD) — see calendar-utils#todayIso. */
  today: string
  rate: RateData | undefined
  currency: string
  date: string
  dayOfWeek: string
  isWeekend?: boolean
  isToday?: boolean
  onClick?: () => void
  comparisonValue?: number | null
  comparisonMode?: string
  occupancy?: OccupancyData
  propertyOccupancy?: PropertyOccupancyData
  roomType?: PopupRoomType
  adr?: number | null
  lastYearAdr?: number | null
  /** The property's backend-served canonical path — threaded to the day popup's target link. */
  propertyBasePath?: string
}

export default function RateCell({ rate, currency, date, dayOfWeek, isWeekend, isToday, onClick, comparisonValue, comparisonMode, occupancy, propertyOccupancy, roomType, adr, lastYearAdr, propertyBasePath, today }: RateCellProps) {
  if (!rate) {
    return (
      <td className={cn(
        'border-b border-r border-border/30 px-1.5 py-2 text-center text-sm text-muted-foreground/50',
        isToday && 'bg-primary/5',
        isWeekend && !isToday && 'bg-slate-50/50'
      )}>
        -
      </td>
    )
  }

  const baseRate = rate.computationDetails?.base_rate_amount
  const isAboveBase = baseRate != null && rate.rateAmount > baseRate
  const isBelowBase = baseRate != null && rate.rateAmount < baseRate
  const isOverride = rate.source === 'manual_override'
  // A relative override is a different promise from a pinned price — it tracks
  // the engine — so the cell says which one is holding this day.
  const relativePct = isOverride ? overridePct(rate.computationDetails?.adjustments as never) : null
  const sym = currencySymbol(currency)
  const hasTarget = hasTargetAdjustment(rate.computationDetails?.adjustments as Array<{ stage: string; modifier_pct?: number }> | undefined)
  // Clearance is running on this day right now. The depth is printed exactly as the backend
  // stamped it — no abs(), no rounding, no sign flip (plan §7; the hover card carries the words).
  const clearancePct = rate.clearanceOffsetPct

  const cellBg = cn(
    rate.stopSell && 'bg-slate-200/80',
    !rate.stopSell && isAboveBase && 'bg-emerald-50/80',
    !rate.stopSell && isBelowBase && 'bg-rose-50/80',
    !rate.stopSell && !isAboveBase && !isBelowBase && isToday && 'bg-primary/5',
    !rate.stopSell && !isAboveBase && !isBelowBase && isWeekend && !isToday && 'bg-slate-50/50',
    isOverride && 'ring-2 ring-inset ring-blue-400/60'
  )

  return (
    <td className={cn('relative border-b border-r border-border/30 p-0', cellBg)}>
      <PaceDot paceRatio={occupancy?.paceRatio} occPct={occupancy?.occupancyPct} baselinePct={occupancy?.baselineOccPct} className="absolute top-1 right-1" />
      {relativePct != null && !rate.stopSell && (
        <span className="pointer-events-none absolute left-1 top-1 rounded bg-blue-100 px-1 text-[9px] font-semibold tabular-nums leading-tight text-blue-700">
          {overridePctLabel(relativePct)}
        </span>
      )}
      {/* Top-left, the same corner as the relative-override badge — and they can never collide:
          the clearance stage stands aside on any tuple carrying a priced override, so a day that
          shows one can never show the other. The bottom of the cell is the occupancy line's. */}
      {clearancePct != null && !rate.stopSell && (
        <span
          title="Clearance is lowering this price — hover for the reason"
          className="pointer-events-none absolute left-1 top-1 rounded bg-amber-100 px-1 text-[9px] font-semibold tabular-nums leading-tight text-amber-700"
        >
          {depthBadgeLabel(clearancePct)}
        </span>
      )}
      {hasTarget && !rate.stopSell && (
        <span
          title="Target adjusted this rate"
          className="absolute bottom-1 right-1 inline-block h-1.5 w-1.5 rounded-full bg-teal-500 opacity-80"
        />
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex flex-col items-center justify-center px-1 py-1 min-h-[90px] cursor-pointer transition-colors hover:brightness-95" onClick={onClick}>
            {rate.stopSell ? (
              <span className="text-[11px] font-bold text-slate-500 tracking-tight">STOP</span>
            ) : (
              <>
                <span className={cn(
                  'text-[13px] font-semibold tabular-nums',
                  isAboveBase && 'text-emerald-700',
                  isBelowBase && 'text-rose-700',
                  !isAboveBase && !isBelowBase && 'text-foreground',
                  rate.closedToArrival && 'line-through decoration-rose-400'
                )}>
                  {formatRateSym(rate.rateAmount, sym)}
                </span>
                {comparisonValue != null && comparisonMode && comparisonMode !== 'none' && !rate.stopSell && (() => {
                  const diff = rate.rateAmount - comparisonValue
                  if (diff === 0) return null
                  return (
                    <span className={cn(
                      'block text-[9px] tabular-nums leading-tight',
                      diff > 0 ? 'text-emerald-600' : 'text-rose-600'
                    )}>
                      {diff > 0 ? '+' : diff < 0 ? '-' : ''}{formatRateSym(Math.abs(diff), sym)}
                    </span>
                  )
                })()}
                <div className={cn(
                  'flex items-center gap-0.5 mt-0.5',
                  rate.syncStatus === 'synced' ? 'text-emerald-600' :
                  rate.syncStatus === 'pending' ? 'text-amber-500' : 'text-muted-foreground/50'
                )}>
                  <span className={cn(
                    'inline-block h-1 w-1 rounded-full',
                    rate.syncStatus === 'synced' ? 'bg-emerald-500' :
                    rate.syncStatus === 'pending' ? 'bg-amber-500' : 'bg-slate-300'
                  )} />
                  <span className="text-[9px] tabular-nums">
                    {rate.pushedRateAmount != null ? formatRateSym(rate.pushedRateAmount, sym) : '--'}
                  </span>
                </div>
                {occupancy && (
                  <span className={cn(
                    'text-[9px] tabular-nums mt-0.5',
                    occupancy.occupancyPct >= 80 ? 'text-emerald-600' :
                    occupancy.occupancyPct >= 60 ? 'text-amber-600' : 'text-muted-foreground/60'
                  )}>
                    {occupancy.occupancyPct}% · {occupancy.booked}/{occupancy.available}
                  </span>
                )}
              </>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          sideOffset={4}
          className="max-w-none overflow-visible border-none bg-transparent p-0 shadow-none"
        >
          <CalendarDayPopup today={today} date={date} dayOfWeek={dayOfWeek} currency={currency} rate={rate} occupancy={occupancy} propertyOccupancy={propertyOccupancy} roomType={roomType} adr={adr} lastYearAdr={lastYearAdr} propertyBasePath={propertyBasePath} />
        </TooltipContent>
      </Tooltip>
    </td>
  )
}
